import React, { useRef, useEffect } from "react";
import * as THREE from "three";

const vertexShader = `
  void main() {
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = `
  uniform float u_time;
  uniform vec2 u_resolution;
  uniform vec2 u_mouse;
  uniform vec3 u_glowColor;
  
  // Simplex 2D noise
  vec3 permute(vec3 x) { return mod(((x*34.0)+1.0)*x, 289.0); }
  float snoise(vec2 v){
    const vec4 C = vec4(0.211324865405187, 0.366025403784439,
             -0.577350269189626, 0.024390243902439);
    vec2 i  = floor(v + dot(v, C.yy) );
    vec2 x0 = v -   i + dot(i, C.xx);
    vec2 i1;
    i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
    vec4 x12 = x0.xyxy + C.xxzz;
    x12.xy -= i1;
    i = mod(i, 289.0);
    vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 ))
    + i.x + vec3(0.0, i1.x, 1.0 ));
    vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy),
      dot(x12.zw,x12.zw)), 0.0);
    m = m*m ;
    m = m*m ;
    vec3 x = 2.0 * fract(p * C.www) - 1.0;
    vec3 h = abs(x) - 0.5;
    vec3 ox = floor(x + 0.5);
    vec3 a0 = x - ox;
    m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
    vec3 g;
    g.x  = a0.x  * x0.x  + h.x  * x0.y;
    g.yz = a0.yz * x12.xz + h.yz * x12.yw;
    return 130.0 * dot(m, g);
  }
  
  void main() {
    vec2 st = (gl_FragCoord.xy - u_resolution * 0.5) / 500.0 + 0.5;
    vec2 center = vec2(0.5, 0.5);
    
    float shimmer = snoise(st * 2.0 + u_time * 0.3) * 0.04;
    
    vec2 mousePos = (u_mouse - u_resolution * 0.5) / 500.0 + 0.5;
    vec2 mouseVector = st - mousePos;
    float mouseDist = length(mouseVector);
    float mouseInfluence = exp(-mouseDist * 2.0) * 0.2;
    
    vec2 distortedSt = st + shimmer * vec2(cos(u_time), sin(u_time)) + mouseVector * mouseInfluence;
    
    float dist = distance(distortedSt, center);
    
    float radius = 0.4;
    float circle = smoothstep(radius, radius - 0.4, dist);
    
    gl_FragColor = vec4(u_glowColor, circle);
  }
`;

const PURPLE_GLOW = new THREE.Color(0.9137, 0.7255, 1.0);
const DARK_GLOW = new THREE.Color(0.5, 0.5, 0.5);

export const Gradient: React.FC<{ top?: string; dark?: boolean }> = ({
  top = "-180px",
  dark = false,
}) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.OrthographicCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const materialRef = useRef<THREE.ShaderMaterial | null>(null);
  const frameIdRef = useRef<number | null>(null);

  let lastMouse = new THREE.Vector2();
  let velocity = new THREE.Vector2();

  useEffect(() => {
    if (!mountRef.current) return;

    const width = 500;
    const height = 500;

    sceneRef.current = new THREE.Scene();
    cameraRef.current = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    rendererRef.current = new THREE.WebGLRenderer({ alpha: true });
    rendererRef.current.setSize(width, height);
    mountRef.current.appendChild(rendererRef.current.domElement);

    const geometry = new THREE.PlaneGeometry(2, 2);
    materialRef.current = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        u_time: { value: 0 },
        u_resolution: { value: new THREE.Vector2(width, height) },
        u_mouse: { value: new THREE.Vector2(width / 2, height / 2) },
        u_glowColor: {
          value: (dark ? DARK_GLOW : PURPLE_GLOW).clone(),
        },
      },
      transparent: true,
    });

    const mesh = new THREE.Mesh(geometry, materialRef.current);
    sceneRef.current.add(mesh);

    const animate = (time: number) => {
      if (
        materialRef.current &&
        rendererRef.current &&
        sceneRef.current &&
        cameraRef.current
      ) {
        materialRef.current.uniforms.u_time.value = time / 1000;
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
      frameIdRef.current = requestAnimationFrame(animate);
    };

    frameIdRef.current = requestAnimationFrame(animate);

    const handleMouseMove = (event: MouseEvent) => {
      if (
        materialRef.current &&
        mountRef.current &&
        materialRef.current.uniforms &&
        materialRef.current.uniforms.u_mouse
      ) {
        const rect = mountRef.current.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        const mousePos = new THREE.Vector2(x, height - y);
        velocity.subVectors(mousePos, lastMouse);
        materialRef.current.uniforms.u_mouse.value.set(x, height - y);
        lastMouse.copy(mousePos);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      if (frameIdRef.current) cancelAnimationFrame(frameIdRef.current);
      window.removeEventListener("mousemove", handleMouseMove);
      if (mountRef.current && rendererRef.current) {
        mountRef.current.removeChild(rendererRef.current.domElement);
      }
    };
  }, []);

  useEffect(() => {
    if (materialRef.current) {
      materialRef.current.uniforms.u_glowColor.value.copy(
        dark ? DARK_GLOW : PURPLE_GLOW
      );
    }
  }, [dark]);

  return (
    <div
      ref={mountRef}
      style={{
        width: "500px",
        height: "500px",
        position: "absolute",
        left: "50%",
        top: top,
        transform: "translate(-50%, 0%) scale(1.0)",
        zIndex: -1,
      }}
    />
  );
};
